import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonIcon,
  ButtonSelect,
  Icon,
  InputField,
  Tooltip,
  useTooltipText,
} from '@saviynt/design-system';

import type { UseTooltipTextReturn } from '@saviynt/design-system';

import {
  type SaviMessage,
  useChatOverlayContext,
} from '../../../../context/chatOverlayContext';
import { useIspmErrorContext } from '../../../../context/ispmErrorContext';
import { ErrorTypeEnum } from '../../../../data-layer/types';
import { useDownloadApi } from '../../../../hooks';
import { Logger } from '../../../../services';
import {
  getChatHistory,
  saveChatHistory,
} from '../../../../services/chatHistory';
import { TABLE_MAX_ROWS } from '../../../../services/ispmUtil';
import { SaveToDashboard } from '../../chart-panel';
import {
  type GeneratedContentType,
  GeneratedContentTypeEnum,
} from '../../types';

import './TopHeaderPanel.css';

TopHeaderPanel.defaultProps = {
  numRecords: undefined,
  headerText: '',
  toggleSelection: undefined,
};

export default function TopHeaderPanel({
  headerText,
  numRecords,
  toggleSelection,
  panelType,
}: {
  headerText?: string;
  numRecords?: number | undefined;
  toggleSelection?: number;
  panelType: GeneratedContentType;
}) {
  // TODO: display icons based on props/state.
  const { chatOverlayState, updateChatOverlayState } = useChatOverlayContext();
  const [showInput, setShowInput] = useState<boolean>(false);
  const [leftPanelheader, setLeftPanelHeader] = useState<string>();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const { updateIspmErrorState } = useIspmErrorContext();

  const isPanelLoading =
    panelType === GeneratedContentTypeEnum.GENERATING_CONTENT;
  const isTablePanel = panelType === GeneratedContentTypeEnum.TABLE;
  const isChartPanel = panelType === GeneratedContentTypeEnum.CHART;

  const overflowingText = () => {
    if (tooltipRef.current) {
      const element = tooltipRef.current;
      const actualWidth = element.clientWidth;

      const computedStyle = window.getComputedStyle(element);
      const cssWidth = parseFloat(computedStyle.width);

      setShowTooltip(actualWidth < cssWidth);
    }
  };

  useLayoutEffect(() => {
    overflowingText();
  }, [showInput]);

  useEffect(() => {
    setLeftPanelHeader(headerText);
  }, [headerText]);

  // type guard to ensure query exists in contentPanelData
  const query =
    'query' in chatOverlayState.contentPanelData
      ? chatOverlayState.contentPanelData.query
      : '';

  const handleCopyTextToClipboard = async () => {
    if (!query) {
      Logger.warn('No query to copy to clipboard');

      return;
    }

    try {
      await navigator.clipboard.writeText(query);
      updateChatOverlayState('successAction', {
        type: 'clipboardCopy',
      });
    } catch (error) {
      Logger.error('Failed to copy text to clipboard:', error);
      // TODO: are we showing an error banner here?
    }
  };

  // TODO: update hook to return the updatedText to be used as a var.
  const {
    tooltipText,
    handleTooltipTriggerClick,
    handleTooltipClose,
    tooltipCloseDelay,
    tooltipCloseDelayAfterClick,
  }: UseTooltipTextReturn = useTooltipText({
    initialText: 'Copy to Clipboard',
    updatedText: 'Copied',
    closeDelay: 50,
    closeDelayAfterClick: 5000,
  });

  const { handleDownload } = useDownloadApi();

  const handleKeyDownHeader = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      const prevHeader = headerText;
      const chatHistory = getChatHistory();

      if (
        chatHistory.length > 0 &&
        !(prevHeader === leftPanelheader) &&
        leftPanelheader !== ''
      ) {
        const updatedChatHistory = chatHistory.map((item) => {
          if (!item.isUser) {
            if ('request' in item.data) {
              const updatedItem = {
                ...item,
                data: {
                  ...item.data,
                  request: {
                    ...item.data.request,
                    freeText: leftPanelheader,
                  },
                },
              };

              return updatedItem as SaviMessage;
            }
          }

          return item;
        });

        saveChatHistory(updatedChatHistory);
        updateChatOverlayState('messages', updatedChatHistory);
      } else {
        setLeftPanelHeader(headerText);
      }

      setShowInput(false);
    }
  };

  const handleHeaderChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { value } = e.target as HTMLInputElement;

    setLeftPanelHeader(value);
  };

  const handleDownloadClick = async () => {
    const result = await handleDownload(query);

    if (!result.success) {
      Logger.error('Download failed:', result.error);
      // show system error banner for now
      updateIspmErrorState('errorType', ErrorTypeEnum.System);
    } else if (result.message) {
      // download will be available later
      updateChatOverlayState('successAction', {
        type: 'downloadPending',
      });
    }
    // no download success alert banner shown - browser will download zip file.
  };

  return (
    <header className='Ispm-TopHeaderPanel-container'>
      <div className='Ispm-TopHeaderPanel'>
        <div className='Ispm-TopHeaderPanel-title'>
          {showInput ? (
            <div className='Ispm-TopHeaderPanel-title--isEditing'>
              <InputField
                className='Ispm-TopHeaderPanel-title--isEditing-input'
                name='basic'
                placeholder='Placeholder'
                value={leftPanelheader}
                setValue={setLeftPanelHeader}
                onChange={handleHeaderChange}
                onKeyDown={handleKeyDownHeader}
              />
            </div>
          ) : (
            <>
              <div
                ref={tooltipRef}
                data-testid='title'
                className='Ispm-TopHeaderPanel-title-tooltip'>
                {showTooltip ? (
                  <Tooltip
                    text={leftPanelheader}
                    placement='top'
                    trigger={
                      <div
                        data-testid='title'
                        className='Ispm-TopHeaderPanel-title-tooltip'>
                        {leftPanelheader}
                      </div>
                    }
                  />
                ) : (
                  leftPanelheader
                )}
              </div>
              {leftPanelheader && (
                <Tooltip
                  text='Rename'
                  placement='top'
                  trigger={
                    <ButtonIcon
                      kind='ghost'
                      icon={<Icon kind='Edit' color='neutral-1000' />}
                      onClick={() => {
                        setShowInput(true);
                      }}
                    />
                  }
                />
              )}
            </>
          )}
        </div>

        <div className='Ispm-TopHeaderPanel-controls'>
          {!isPanelLoading && (
            <>
              {toggleSelection === 1 && isTablePanel && (
                // only show download icon when the sql is showing
                <Tooltip
                  text='Download'
                  placement='bottom'
                  trigger={
                    <ButtonSelect
                      kind='icon'
                      size='medium'
                      prefixIcon={
                        <Icon kind='Download' size='medium' color='info-500' />
                      }
                      onClick={handleDownloadClick}
                    />
                  }
                />
              )}
              {toggleSelection === 1 && isChartPanel && (
                // only show download icon when the sql is showing
                <SaveToDashboard
                  chartName={leftPanelheader ?? headerText ?? ''}
                />
              )}
              {toggleSelection === 2 && (
                // show copy icon when sql statement is showing
                <Tooltip
                  text={tooltipText}
                  placement='bottom'
                  closeDelay={tooltipCloseDelay}
                  closeDelayAfterClick={tooltipCloseDelayAfterClick}
                  onClose={handleTooltipClose}
                  isDisabled={tooltipText === 'Copied'}
                  trigger={
                    <ButtonSelect
                      kind='icon'
                      size='medium'
                      prefixIcon={
                        <Icon
                          kind={tooltipText === 'Copied' ? 'check' : 'Copy'}
                          color='neutral-1000'
                        />
                      }
                      onClick={async () => {
                        handleTooltipTriggerClick();
                        await handleCopyTextToClipboard();
                      }}
                    />
                  }
                />
              )}
            </>
          )}
          <Tooltip
            text='Collapse'
            placement='bottom'
            trigger={
              <ButtonIcon
                kind='ghost'
                icon={<Icon kind='ExpandLeft' color='neutral-1000' />}
                onClick={() => {
                  if (chatOverlayState.isDataPanelFullScreen) {
                    updateChatOverlayState('isDataPanelFullScreen', false);
                  } else {
                    updateChatOverlayState('showGeneratedContent', false);
                  }
                }}
              />
            }
          />

          {!chatOverlayState.isDataPanelFullScreen && (
            <Tooltip
              text='Expand'
              placement='top'
              trigger={
                <ButtonIcon
                  kind='ghost'
                  icon={<Icon kind='ExpandRight' color='neutral-1000' />}
                  onClick={() => {
                    updateChatOverlayState('isDataPanelFullScreen', true);
                  }}
                />
              }
            />
          )}
        </div>
      </div>
      {Boolean(
        panelType === GeneratedContentTypeEnum.TABLE &&
          numRecords &&
          numRecords >= TABLE_MAX_ROWS
      ) && (
        <Box className='Ispm-TopHeaderPanel-iconInfoWrapper'>
          <Tooltip
            text='Filter for inactive users by checking status...'
            placement='bottom'
            trigger={<Icon kind='Info' color='info-700' size='xSmall' />}
          />
          <span className='Ispm-TopHeaderPanel-iconInfoWrapper-infoText'>
            This is a sample of the dataset. Download to view the complete
            table.
          </span>
        </Box>
      )}
    </header>
  );
}
